// src/config/brand.ts
//
// Sistema de "white-label" / branding multi-marca.
// Se selecciona la marca activa con la variable de entorno `VITE_BRAND_NAME`.
// Si el valor es inválido o está vacío, se aplica el fallback por defecto
// (AINOVIS) y se avisa por consola en desarrollo.
//
// Los COLORES viven en src/brands/<marca>.css (cargado por BrandProvider); los
// LOGOS se resuelven por marca+tema en src/config/brand-logos.ts.

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

import { BRAND_LOGOS } from './brand-logos';
import type { BrandLogos } from './brand-logos';

/** Identificadores internos de marca (lo que va en VITE_BRAND_NAME). */
export type BrandId = 'AINOVIS' | 'ALMA_MEDICAL';

export interface BrandConfig {
  /** Identificador interno (lo que se compara con VITE_BRAND_NAME). */
  id: BrandId;
  /** Nombre para mostrar en la UI en castellano. */
  displayName: string;
  /** Nombre para mostrar en inglés. */
  displayNameEn: string;
  /**
   * Nombre de PRODUCTO de una sola palabra (p. ej. "Ainovis" / "ALMA"). Se usa
   * como sustituto de la palabra "Ainovis" embebida en las traducciones y en
   * textos de UI hardcodeados, para que la marca activa se refleje en runtime.
   */
  productName: string;
  /**
   * Nombre del ASISTENTE de IA (p. ej. "Carolina" / "ASSISTANT"). Se usa como
   * sustituto de la palabra "Carolina" embebida en traducciones y textos de UI
   * hardcodeados (greetings, tooltips, etc), igual que productName con "Ainovis".
   * Para la marca por defecto (Ainovis) se mantiene "Carolina".
   */
  assistantName: string;
  /**
   * Paleta de color de marca en formato JS (hex + tripleta rgb). Es el espejo
   * de los tokens CSS definidos en src/brands/<marca>.css. Úsalo SOLO cuando
   * necesites el color como string JS (p. ej. props `fill` / `stroke` de
   * recharts: los atributos SVG NO resuelven `var()`). Para reglas CSS sigue
   * siendo preferible consumir las variables `--primary-*` directamente.
   */
  colors: {
    primary: string;
    primaryRgb: string; // "r, g, b" — utilidad para `rgba(${...}, alpha)`.
    primary2?: string;
  };
  /**
   * Paleta de color usada por las gráficas de estadísticas del grupo
   * (producción, modalidades, ranking de radiólogos, tiempo medio…). El
   * primer elemento SIEMPRE es el primario de la marca para que la serie
   * dominante enlace con la identidad visual; el resto son tonos decorativos
   * fijos que aportan contraste entre categorías.
   *
   * Úsalo desde componentes de analytics cuando necesites pintar series
   * múltiples (SVG attributes como `fill`/`stroke` no resuelven `var()`).
   */
  chartColors: string[];
  /** Título del navegador (document.title) y nombre corto de pestaña. */
  documentTitle: string;
  /** Assets de logo (full/mono/iso por tema + favicon). Ver brand-logos.ts. */
  logos: BrandLogos;
}

// ---------------------------------------------------------------------------
// Registro de marcas
// ---------------------------------------------------------------------------

export const BRANDS: Record<BrandId, BrandConfig> = {
  AINOVIS: {
    id: 'AINOVIS',
    displayName: 'Ainovis',
    displayNameEn: 'Ainovis',
    productName: 'Ainovis',
    assistantName: 'Carolina',
    colors: {
      primary: '#3F52FF',
      primaryRgb: '63, 82, 255',
      primary2: '#5B6BFF',
    },
    // Primario de Ainovis + paleta decorativa de analytics.
    chartColors: ['#3F52FF', '#2ED0FF', '#7C5CFF', '#00C2A8', '#FFB020', '#FF6692'],
    documentTitle: 'Ainovis One',
    logos: BRAND_LOGOS.AINOVIS,
  },
  ALMA_MEDICAL: {
    id: 'ALMA_MEDICAL',
    displayName: 'ALMA Medical',
    displayNameEn: 'ALMA Medical',
    productName: 'ALMA',
    assistantName: 'ALBA',
    colors: {
      primary: '#FF9103',
      primaryRgb: '255, 145, 3',
      primary2: '#FFB04A',
    },
    // Primario naranja de ALMA + paleta decorativa coherente con la marca.
    chartColors: ['#FF9103', '#00C2A8', '#3F52FF', '#7C5CFF', '#FFB020', '#FF6692'],
    documentTitle: 'Alma AI Reporting',
    logos: BRAND_LOGOS.ALMA_MEDICAL,
    // TODO(brand): ajustar paleta final de ALMA Medical cuando Marketing la confirme.
  },
};

/** Marca por defecto si VITE_BRAND_NAME es inválido o no está definida. */
export const DEFAULT_BRAND_ID: BrandId = 'AINOVIS';

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Normaliza el valor de la env a un BrandId conocido.
 * - Trim + UPPERCASE.
 * - Si no está en el registro -> DEFAULT_BRAND_ID (y avisa en dev).
 */
export function resolveBrandId(rawValue: string | undefined): BrandId {
  const normalized = (rawValue ?? '').trim().toUpperCase();
  if (normalized && (Object.keys(BRANDS) as BrandId[]).includes(normalized as BrandId)) {
    return normalized as BrandId;
  }
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[brand] VITE_BRAND_NAME="${rawValue ?? ''}" no es una marca válida. ` +
        `Usando el fallback por defecto "${DEFAULT_BRAND_ID}". ` +
        `Valores permitidos: ${Object.keys(BRANDS).join(', ')}.`,
    );
  }
  return DEFAULT_BRAND_ID;
}

/** Devuelve la configuración completa de una marca dado su id. */
export function getBrandConfig(id: BrandId): BrandConfig {
  return BRANDS[id] ?? BRANDS[DEFAULT_BRAND_ID];
}
